import {
  Box,
  Button,
  Collapse,
  FormHelperText,
  Grid,
  Input,
  InputLabel,
  LinearProgress,
  OutlinedInput,
  Typography,
} from "@mui/material";
import {
  ReactNode,
  SyntheticEvent,
  useCallback,
  useMemo,
  useState,
} from "react";
import Swal from "sweetalert2";
import { SubmitHandler, useForm } from "react-hook-form";
import grey from "@mui/material/colors/grey";
import { RolePositionType } from "../types/role-position.type";
import { orange } from "@mui/material/colors";
import { CareerSubmitPayload } from "../types/career-submit-payload.type";
import { apiClient } from "../clients/api.client";
import { AxiosProgressEvent } from "axios";
import { Base64 } from "js-base64";
import { useTranslation } from "react-i18next";

function DarkBgWrapper({ children }: { children: ReactNode }) {
  return (
    <>
      <Box
        sx={{
          position: "relative",
          bgcolor: grey[200],
          display: "flex",
          overflowX: "scroll",
          overflowY: "hidden",
          width: "100%",
          textAlign: "left",
          px: 5,
          py: 3,
          borderRadius: "10px",
          scrollbarWidth: "1px",
          "&::-webkit-scrollbar": {
            height: "2px",
            position: "fixed",
          },
          "&::-webkit-scrollbar-thumb": {
            height: "2px",
            position: "fixed",
            background: "rgba(0,0,0,0.3)",
          },
        }}
      >
        {children}
      </Box>
    </>
  );
}

export function CareerForm(props: {}) {
  const {
    register,
    setValue,
    watch,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<CareerSubmitPayload>();
  const isCn = window.location.search.includes("cnLang");
  const [lang, setLang] = useState(isCn ? "zh" : "en");
  const { t } = useTranslation("", { lng: lang });

  const [progress, setProgress] = useState<undefined | number>();
  const [isFormProcessing, setIsFormProcessing] = useState<boolean>(false);
  const fileReader = useMemo(() => new FileReader(), []);
  const onFileInputChange = useCallback(
    (fieldName: keyof CareerSubmitPayload, event: SyntheticEvent<FileList>) => {
      // @ts-ignore
      const fileData = event.target?.files as FileList;
      if (!fileData || !fileData.length) return;

      fileReader.onload = (ev) => {
        console.log(ev?.target?.result);
      };
      fileReader.onerror = (ev) => {
        setError(fieldName, {
          message: "Unable to parse form data",
          type: "additional",
        });
      };
      return true;
    },
    [fileReader, setError],
  );
  const parsePayload = async (data?: FileList): Promise<string | null> =>
    new Promise((resolve, reject) => {
      if (!data || data.length === 0) {
        resolve(null);
        return;
      }

      const fileReader = new FileReader();
      fileReader.onload = (ev: ProgressEvent<FileReader>) => {
        if (!ev?.target?.result) resolve(null);
        resolve(
          Base64.fromUint8Array(
            new Uint8Array(ev?.target?.result as ArrayBuffer),
          ),
        );
      };
      fileReader.onerror = reject;
      fileReader.readAsArrayBuffer((data as FileList).item(0) as File);
    });

  const onSubmit: SubmitHandler<CareerSubmitPayload> = useCallback(
    async (data) => {
      if (isFormProcessing) {
        return;
      }
      const resumePayload: string | null = await parsePayload(data.resume);
      const coverLetterPayload: string | null = await parsePayload(
        data.coverLetter,
      );

      const payload: CareerSubmitPayload = {
        ...data,
        resumePayload: data.resume.item(0) && {
          content: resumePayload,
          filename: data.resume.item(0)?.name,
        },
        coverLetterPayload: data.coverLetter?.item(0) && {
          content: coverLetterPayload,
          filename: data.coverLetter.item(0)?.name,
        },
      };

      setIsFormProcessing(true);
      try {
        const response = await apiClient
          .post("/api/careers/post.json", payload, {
            onUploadProgress: (progressEvent: AxiosProgressEvent) => {
              setProgress(progressEvent.progress);
            },
            responseType: "json",
          })
          .finally(() => {
            setIsFormProcessing(false);
          });
        if (response.data.ok) {
          Swal.fire({
            title: "Thank you!",
            text: response.data.message,
            icon: "success",
          });
        } else {
          Swal.fire({
            icon: "warning",
            title: "Oops...",
            text: response.data.message,
          });
        }
      } catch (e) {
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "Something went wrong. Please try again.",
        });
      }
    },
    [isFormProcessing],
  );

  const selectedRole = watch("selectedRole");
  const isFieldHasError = useCallback(
    (fieldName: string) => {
      // @ts-ignore
      return !!errors?.[fieldName];
    },
    [errors],
  );

  const handleOnSelectRole = (role: RolePositionType) => {
    setValue("selectedRole", role);
  };
  const isRoleSelected = useCallback(
    (role: RolePositionType) =>
      JSON.stringify(role) === JSON.stringify(selectedRole),
    [selectedRole],
  );

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={1} sx={{ alignItems: "center", mb: 20 }}>
          <Grid item xs={12} mt={0} mb={5}>
            <DarkBgWrapper>
              <Grid container spacing={2} py={3}>
                <>
                  {/*<!-- Fname -->*/}
                  <Grid item xs={6} sm={2} md={3} lg={2}>
                    <Typography textAlign={"left"}>
                      <InputLabel htmlFor={"fName"}>
                        {t("First Name")}
                      </InputLabel>
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={10} md={9} lg={10}>
                    <OutlinedInput
                      className={"input-field"}
                      sx={{ bgcolor: "white", borderRadius: "10px" }}
                      id={"fName"}
                      error={isFieldHasError("fName")}
                      fullWidth
                      size={"small"}
                      {...register("fName", {
                        required: {
                          value: true,
                          message: "Value must provided.",
                        },
                        maxLength: 250,
                      })}
                    />
                    <Collapse in={!!errors?.fName?.message}>
                      {errors?.fName?.message && (
                        <FormHelperText>
                          {errors?.fName?.message}
                        </FormHelperText>
                      )}
                    </Collapse>
                  </Grid>
                </>

                <>
                  {/*<!-- Lname -->*/}
                  <Grid item xs={6} sm={2} md={3} lg={2}>
                    <Typography textAlign={"left"}>
                      <InputLabel htmlFor={"lName"}>
                        {t("Last Name")}
                      </InputLabel>
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={10} md={9} lg={10}>
                    <OutlinedInput
                      className={"input-field"}
                      sx={{ bgcolor: "white", borderRadius: "10px" }}
                      id={"lName"}
                      error={isFieldHasError("lName")}
                      fullWidth
                      size={"small"}
                      {...register("lName", {
                        required: {
                          value: true,
                          message: "Value must provided.",
                        },
                        maxLength: 250,
                      })}
                    />
                    <Collapse in={!!errors?.lName?.message}>
                      {errors?.lName?.message && (
                        <FormHelperText>
                          {errors?.lName?.message}
                        </FormHelperText>
                      )}
                    </Collapse>
                  </Grid>
                </>
                <>
                  {/*<!-- email -->*/}
                  <Grid item xs={6} sm={2} md={3} lg={2}>
                    <Typography textAlign={"left"}>
                      <InputLabel htmlFor={"email"}>{t("Email")}</InputLabel>
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={10} md={9} lg={10}>
                    <OutlinedInput
                      {...register("email", {
                        required: {
                          value: true,
                          message: "Value must provided.",
                        },
                        maxLength: 250,
                        pattern: {
                          value: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/,
                          message: "Email must be valid format.",
                        },
                      })}
                      className={"input-field"}
                      sx={{ bgcolor: "white", borderRadius: "10px" }}
                      id={"email"}
                      error={isFieldHasError("email")}
                      fullWidth
                      size={"small"}
                      type={"email"}
                    />
                    <Collapse in={!!errors?.email?.message}>
                      {errors?.email?.message && (
                        <FormHelperText>
                          {errors?.email?.message}
                        </FormHelperText>
                      )}
                    </Collapse>
                  </Grid>
                </>
                <>
                  {/*<!-- Mobile -->*/}
                  <Grid item xs={6} sm={2} md={3} lg={2}>
                    <Typography textAlign={"left"}>
                      <InputLabel htmlFor={"email"}>{t("Mobile")}</InputLabel>
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={10} md={9} lg={10}>
                    <OutlinedInput
                      {...register("mobile", {
                        required: {
                          value: true,
                          message: "Value must provided.",
                        },
                        pattern: {
                          value: /^[6|8|9][0-9]{7}$/,
                          message: "Please provide a correct mobile number.",
                        },
                        minLength: 8,
                        maxLength: 8,
                      })}
                      className={"input-field"}
                      sx={{ bgcolor: "white", borderRadius: "10px" }}
                      id={"email"}
                      error={isFieldHasError("mobile")}
                      fullWidth
                      size={"small"}
                      type={"tel"}
                    />
                    <Collapse in={!!errors?.mobile?.message}>
                      {errors?.mobile?.message && (
                        <FormHelperText>
                          {errors?.mobile?.message}
                        </FormHelperText>
                      )}
                    </Collapse>
                  </Grid>
                </>

                <>
                  {/*<!-- Qualification -->*/}
                  <Grid item xs={6} sm={2} md={3} lg={2}>
                    <Typography textAlign={"left"}>
                      <InputLabel htmlFor={"qualification"}>
                        {t("Qualification")}
                      </InputLabel>
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={10} md={9} lg={10}>
                    <OutlinedInput
                      className={"input-field"}
                      sx={{ bgcolor: "white", borderRadius: "10px" }}
                      id={"qualification"}
                      error={isFieldHasError("qualification")}
                      fullWidth
                      size={"small"}
                      {...register("qualification", {
                        required: {
                          value: true,
                          message: "Value must provided.",
                        },
                      })}
                    />

                    <Collapse in={!!errors?.qualification?.message}>
                      {errors?.qualification?.message && (
                        <FormHelperText>
                          {errors?.qualification?.message}
                        </FormHelperText>
                      )}
                    </Collapse>
                  </Grid>
                </>
                <>
                  {/*<!-- Resume -->*/}
                  <Grid item xs={6} sm={2} md={3} lg={2}>
                    <Typography textAlign={"left"}>
                      <InputLabel htmlFor={"resume"}>
                        {t("Resume/CV")}
                      </InputLabel>
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={10} md={9} lg={10}>
                    <Input
                      {...register("resume", {
                        onChange: (event) => onFileInputChange("resume", event),
                        required: {
                          value: true,
                          message: "File need to be provided.",
                        },
                        validate: {
                          fileType: (value, formValues) => {
                            if (!value) return true;
                            if (
                              !".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf"
                                .split(",")
                                .includes("" + value.item(0)?.type)
                            ) {
                              return "Please provide a correct file.";
                            }
                            return;
                          },
                        },
                      })}
                      className={"input-field"}
                      id={"resume"}
                      error={isFieldHasError("resume")}
                      fullWidth
                      size={"small"}
                      type={"file"}
                      inputProps={{
                        accept:
                          ".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf",
                      }}
                      endAdornment={
                        <LinearProgress color="success" value={progress} />
                      }
                    />

                    <Collapse in={!!errors?.resume?.message}>
                      {errors?.resume?.message && (
                        <FormHelperText>
                          {errors?.resume?.message}
                        </FormHelperText>
                      )}
                    </Collapse>
                  </Grid>
                </>

                <>
                  {/*<!-- Cover Letter -->*/}
                  <Grid item xs={6} sm={2} md={3} lg={2}>
                    <Typography textAlign={"left"}>
                      <InputLabel htmlFor={"coverLetter"}>
                        {t("Cover letter")}
                      </InputLabel>
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={10} md={9} lg={10}>
                    <Input
                      {...register("coverLetter", {
                        onChange: (event) =>
                          onFileInputChange("coverLetter", event),
                        validate: {
                          fileType: (value, formValues) => {
                            if (!value || !value?.item(0) || !value.length)
                              return true;
                            if (
                              !".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf"
                                .split(",")
                                .includes("" + value.item(0)?.type)
                            ) {
                              return "Please provide a correct file.";
                            }
                            return;
                          },
                        },
                      })}
                      className={"input-field"}
                      id={"coverLetter"}
                      error={isFieldHasError("coverLetter")}
                      fullWidth
                      size={"small"}
                      type={"file"}
                      inputProps={{
                        accept:
                          ".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf",
                        draggable: true,
                      }}
                    />

                    <Collapse in={!!errors?.coverLetter?.message}>
                      {errors?.coverLetter?.message && (
                        <FormHelperText>
                          {errors?.coverLetter?.message}
                        </FormHelperText>
                      )}
                    </Collapse>
                  </Grid>
                </>
              </Grid>
            </DarkBgWrapper>
          </Grid>
          <Grid item xs={12}>
            <>
              <Grid item xs={12}>
                <Button
                  disabled={isFormProcessing}
                  disableElevation={true}
                  variant={"contained"}
                  type={"submit"}
                  sx={{
                    bgcolor: orange[400],
                    fontWeight: 900,
                    borderRadius: "15px",
                    py: 2,
                    px: 8,
                    color: "white",
                    "&:hover": { bgcolor: orange[600] },
                  }}
                >
                  {t("Apply Now")}
                </Button>
              </Grid>
            </>
          </Grid>
        </Grid>
      </form>
    </>
  );
}
